import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25DndSortableModule } from "../../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import {
    TrailApi,
    type AvailabilityOperator,
    type CohortAvailabilityRule,
    type CohortDto,
    type CurricCopyMode,
    type CurriculumListItem,
    type StudentCohortDetailDto,
    type StudentDeclarationDto,
    type TrackDetail,
    type TrailDto,
} from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { dropdownItemFromId, idFromDropdownItem } from "./trail.dropdown.util";
import {
    changedSortOrders,
    createSortOrderSaveQueue,
    nextAppendSortOrder,
    SORT_ORDER_PERSIST_DEBOUNCE_MS,
    uniqueSortOrdersForInsertSet,
} from "../../../util/sort.order.util";
import { UserStore } from "../../../stores/user.store";
import { formatUserprefDate, toDateInputValue } from "./trail.userpref.date";
import {
    editorNodeToSaveTree,
    wireTreeToEditorNode,
    type RequirementGroupNode,
    type RequirementNode,
} from "./trail.requirement.tree.component";
import { TrailRequirementTreeEditorComponent } from "./trail.requirement.tree.editor.component";

/** FA + last two digits of (today + 1 year). */
export function fallCohortCodeExample(now: Date = new Date()): string {
    const nextYearDate = new Date(now);
    nextYearDate.setFullYear(now.getFullYear() + 1);
    return `FA${String(nextYearDate.getFullYear()).slice(-2)}`;
}

export function emptyTrailRequirementTree(): RequirementGroupNode {
    return { nodeType: "group", groupType: "ALL", label: "Root", children: [] };
}

@TypeManagerDecorator("s25-ng-trail-cohort-admin")
@Component({
    selector: "s25-ng-trail-cohort-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25FadingSuccessMsgComponent,
        S25MultiselectModule,
        S25DropdownPaginatedModule,
        S25DndSortableModule,
        S25IconComponent,
        TrailRequirementTreeEditorComponent,
    ],
    host: { class: "trail-cohort-admin" },
    template: `
        <div class="c-padding--double">
            <div class="trail-cohort-admin__header c-margin-bottom--single">
                <h2>{{ labels.COHORT }} Management</h2>
                <s25-ng-button [type]="'outline'" [onClick]="handleBack">← Back</s25-ng-button>
            </div>

            @if (cohortList.isLoading()) {
                <p>Loading…</p>
            } @else if (cohortList.error()) {
                <p class="c-text--error">{{ formatError(cohortList.error()) }}</p>
            } @else {
                <div class="trail-cohort-admin__content">
                    <section class="trail-cohort-admin__list-section">
                        <div class="trail-cohort-admin__list-heading">
                            <h3>{{ labels.COHORTS }}</h3>
                            <s25-ng-button data-id="refresh-list" [type]="'outline'" [onClick]="refreshList">
                                Refresh
                            </s25-ng-button>
                        </div>
                        @if (!(cohortList.value() ?? []).length) {
                            <p class="c-text--secondary">No cohorts defined yet.</p>
                        } @else {
                            <table class="c-table">
                                <thead>
                                    <tr>
                                        <th>Type Code</th>
                                        <th>{{ labels.COHORT }} Code</th>
                                        <th>Effective Start</th>
                                        <th>Effective End</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (cohort of cohortList.value() ?? []; track cohort.cohortId) {
                                        <tr>
                                            <td>{{ cohort.cohortTypeCode }}</td>
                                            <td>{{ cohort.cohortCode }}</td>
                                            <td>{{ formatDate(cohort.effectiveStartDt) }}</td>
                                            <td>{{ formatDate(cohort.effectiveEndDt) }}</td>
                                            <td>
                                                <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [onClick]="editCohortFactory(cohort)"
                                                    >
                                                        Edit
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [onClick]="showRulesFactory(cohort)"
                                                    >
                                                        Availability Rules
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [onClick]="showTrailsFactory(cohort)"
                                                    >
                                                        {{ labels.TRAILS }}
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        data-id="copy-cohort"
                                                        [onClick]="startCopyCohortFactory(cohort)"
                                                    >
                                                        Copy
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        [type]="'danger--outline'"
                                                        data-id="disable-cohort"
                                                        [onClick]="disableCohortFactory(cohort)"
                                                    >
                                                        Disable
                                                    </s25-ng-button>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                        <p class="c-margin-top--single">
                            <s25-ng-button [type]="'primary'" [onClick]="startCreateCohort">
                                New {{ labels.COHORT }}
                            </s25-ng-button>
                        </p>
                    </section>

                    @if (showForm()) {
                        <section
                            class="trail-cohort-admin__form-section c-border c-padding--double c-margin-top--double"
                        >
                            <h3>{{ formCohortId() ? "Edit" : "Create" }} {{ labels.COHORT }}</h3>
                            <div class="form-group c-margin-bottom--single">
                                <label>{{ labels.COHORT }} Type Code *</label>
                                <input
                                    class="c-input"
                                    data-id="cohort-type-code"
                                    [ngModel]="formCohortTypeCode()"
                                    (ngModelChange)="formCohortTypeCode.set($event)"
                                    [placeholder]="cohortTypeCodePlaceholder"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>{{ labels.COHORT }} Code *</label>
                                <input
                                    class="c-input"
                                    data-id="cohort-code"
                                    [ngModel]="formCohortCode()"
                                    (ngModelChange)="formCohortCode.set($event)"
                                    [placeholder]="cohortCodePlaceholder"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Effective Start Date</label>
                                <input
                                    type="date"
                                    class="c-input"
                                    name="cohortEffectiveStart"
                                    data-id="cohort-effective-start"
                                    [ngModel]="formEffectiveStart()"
                                    (ngModelChange)="formEffectiveStart.set($event)"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Effective End Date</label>
                                <input
                                    type="date"
                                    class="c-input"
                                    name="cohortEffectiveEnd"
                                    data-id="cohort-effective-end"
                                    [ngModel]="formEffectiveEnd()"
                                    (ngModelChange)="formEffectiveEnd.set($event)"
                                />
                            </div>
                            <div class="trail-cohort-admin__form-actions">
                                <s25-ng-button [type]="'primary'" [onClick]="saveCohort">Save</s25-ng-button>
                                <s25-ng-button [type]="'danger--outline'" [onClick]="cancelForm">Cancel</s25-ng-button>
                            </div>
                        </section>
                    }

                    @if (copyingCohort(); as sourceCohort) {
                        <section
                            class="trail-cohort-admin__form-section c-border c-padding--double c-margin-top--double"
                        >
                            <h3>Copy {{ sourceCohort.cohortCode }}</h3>
                            <p class="c-text--secondary c-margin-bottom--half">
                                Creates a new empty {{ labels.COHORT }} and copies this entire curriculum into it.
                            </p>
                            <div class="form-group c-margin-bottom--single">
                                <label>{{ labels.COHORT }} Type Code *</label>
                                <input
                                    class="c-input"
                                    data-id="copy-cohort-type-code"
                                    [ngModel]="copyFormTypeCode()"
                                    (ngModelChange)="copyFormTypeCode.set($event)"
                                    [placeholder]="cohortTypeCodePlaceholder"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>{{ labels.COHORT }} Code *</label>
                                <input
                                    class="c-input"
                                    data-id="copy-cohort-code"
                                    [ngModel]="copyFormCode()"
                                    (ngModelChange)="copyFormCode.set($event)"
                                    [placeholder]="cohortCodePlaceholder"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Effective Start Date</label>
                                <input
                                    type="date"
                                    class="c-input"
                                    name="copyEffectiveStart"
                                    data-id="copy-effective-start"
                                    [ngModel]="copyFormEffectiveStart()"
                                    (ngModelChange)="copyFormEffectiveStart.set($event)"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Effective End Date</label>
                                <input
                                    type="date"
                                    class="c-input"
                                    name="copyEffectiveEnd"
                                    data-id="copy-effective-end"
                                    [ngModel]="copyFormEffectiveEnd()"
                                    (ngModelChange)="copyFormEffectiveEnd.set($event)"
                                />
                            </div>
                            <div class="trail-cohort-admin__form-actions">
                                <s25-ng-button [type]="'primary'" [onClick]="saveCopyCohort"
                                    >Copy {{ labels.COHORT }}</s25-ng-button
                                >
                                <s25-ng-button [type]="'danger--outline'" [onClick]="cancelCopyCohort"
                                    >Cancel</s25-ng-button
                                >
                            </div>
                        </section>
                    }

                    <section class="trail-cohort-admin__config-section c-border c-padding--double c-margin-top--double">
                        <h3>{{ labels.COHORT }} Auto-Assign Configuration</h3>
                        <p class="c-text--secondary c-margin-bottom--half">
                            Contact custom attributes used for automatic cohort assignment.
                        </p>
                        <div class="form-group c-margin-bottom--single">
                            <label>Type custom attribute for direct cohort assignment</label>
                            <s25-custom-attr-dropdown
                                [itemTypeId]="contactItemTypeId"
                                [whitelistedTypes]="codeCustAtrbTypes"
                                [hasNoneItem]="true"
                                [ariaLabel]="'Type custom attribute'"
                                [value]="dropdownItemFromId(configTypeCustAtrbId())"
                                (valueChange)="onConfigTypeCustAtrbChange($event)"
                            />
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label>Code custom attribute for direct cohort assignment</label>
                            <s25-custom-attr-dropdown
                                [itemTypeId]="contactItemTypeId"
                                [whitelistedTypes]="codeCustAtrbTypes"
                                [hasNoneItem]="true"
                                [ariaLabel]="'Code custom attribute'"
                                [value]="dropdownItemFromId(configCodeCustAtrbId())"
                                (valueChange)="onConfigCodeCustAtrbChange($event)"
                            />
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label
                                >Date custom attribute for auto-assignment by matching availabilty rules and
                                dates</label
                            >
                            <s25-custom-attr-dropdown
                                [itemTypeId]="contactItemTypeId"
                                [whitelistedTypes]="dateCustAtrbTypes"
                                [hasNoneItem]="true"
                                [ariaLabel]="'Date custom attribute'"
                                [value]="dropdownItemFromId(configDateCustAtrbId())"
                                (valueChange)="onConfigDateCustAtrbChange($event)"
                            />
                        </div>
                        <div class="trail-cohort-admin__config-save">
                            <s25-ng-button [type]="'primary'" [onClick]="saveCohortConfig">Save Config</s25-ng-button>
                            <s25-ng-fading-success-msg [(message)]="configSaveSuccessMessage" />
                        </div>
                    </section>

                    @if (selectedCohortForRules()) {
                        <section
                            class="trail-cohort-admin__rules-section c-border c-padding--double c-margin-top--double"
                        >
                            <h3>Availability Rules for {{ selectedCohortForRules()!.cohortCode }}</h3>
                            @if (matchCountResource.value(); as matchCount) {
                                <p class="c-text--secondary c-margin-bottom--half">
                                    Code match: {{ matchCount.codeMatchCount }} students · Date + availability:
                                    {{ matchCount.dateAvailCount }} students
                                </p>
                            }
                            @for (rule of editingRules(); track $index; let ruleIndex = $index) {
                                <div class="trail-cohort-admin__avail-rule c-margin-bottom--half">
                                    <div class="form-group">
                                        <label>Custom attribute *</label>
                                        <s25-custom-attr-dropdown
                                            [itemTypeId]="contactItemTypeId"
                                            [ariaLabel]="'Custom attribute'"
                                            [value]="dropdownItemFromId(rule.custAtrbId)"
                                            (valueChange)="onRuleCustAtrbChange(ruleIndex, $event)"
                                        />
                                    </div>
                                    <div class="form-group">
                                        <label>Operator</label>
                                        <select
                                            class="c-input"
                                            [ngModel]="rule.operator"
                                            (ngModelChange)="onRuleOperatorChange(ruleIndex, $event)"
                                        >
                                            <option value="IN">In</option>
                                            <option value="NOT_IN">Not In</option>
                                            <option value="CONTAINS">Contains</option>
                                            <option value="EXISTS">Exists</option>
                                            <option value="NOT_EXISTS">Not Exists</option>
                                        </select>
                                    </div>
                                    @if (rule.operator !== "EXISTS" && rule.operator !== "NOT_EXISTS") {
                                        @if (rule.operator === "CONTAINS") {
                                            <div class="form-group">
                                                <label>Value *</label>
                                                <input
                                                    class="c-input"
                                                    data-id="rule-contains-value"
                                                    [attr.aria-label]="'Value'"
                                                    [ngModel]="(rule.values ?? [])[0] ?? ''"
                                                    (ngModelChange)="patchContainsValue(ruleIndex, $event)"
                                                />
                                            </div>
                                        } @else {
                                            <div class="form-group">
                                                <label>Values *</label>
                                                <div class="trail-cohort-admin__rule-values">
                                                    @for (
                                                        value of rule.values ?? [];
                                                        track $index;
                                                        let valueIndex = $index
                                                    ) {
                                                        <div class="trail-cohort-admin__rule-value">
                                                            <input
                                                                class="c-input"
                                                                data-id="rule-in-value"
                                                                [attr.aria-label]="'Value ' + (valueIndex + 1)"
                                                                [ngModel]="value"
                                                                (ngModelChange)="
                                                                    patchRuleValueAt(ruleIndex, valueIndex, $event)
                                                                "
                                                            />
                                                            <button
                                                                type="button"
                                                                class="c-btn--small"
                                                                data-id="rule-in-remove"
                                                                [attr.aria-label]="'Remove value'"
                                                                (click)="removeRuleValue(ruleIndex, valueIndex)"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    }
                                                    <button
                                                        type="button"
                                                        class="c-btn--small"
                                                        data-id="rule-in-add"
                                                        (click)="addRuleValue(ruleIndex)"
                                                    >
                                                        Add value
                                                    </button>
                                                </div>
                                            </div>
                                        }
                                    }
                                    <div class="form-group">
                                        <label>Description</label>
                                        <input
                                            class="c-input"
                                            [ngModel]="rule.catalogDescription ?? ''"
                                            (ngModelChange)="patchRule(ruleIndex, 'catalogDescription', $event)"
                                        />
                                    </div>
                                    <button
                                        class="c-btn--small"
                                        style="color: var(--color--negative-text, #c00);"
                                        (click)="removeRule(ruleIndex)"
                                    >
                                        Remove
                                    </button>
                                </div>
                            }
                            <div class="trail-cohort-admin__rule-actions">
                                <s25-ng-button [type]="'outline'" [onClick]="addRule">Add Rule</s25-ng-button>
                                <s25-ng-button [type]="'primary'" [onClick]="saveRules">Save All Rules</s25-ng-button>
                                <s25-ng-button [type]="'outline'" [onClick]="clearSelectedCohortForRules"
                                    >Close</s25-ng-button
                                >
                            </div>
                        </section>
                    }

                    @if (selectedCohortForTrails()) {
                        <section
                            class="trail-cohort-admin__trails-section c-border c-padding--double c-margin-top--double"
                        >
                            <div
                                style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;"
                            >
                                <h3>{{ labels.TRAILS }} for {{ selectedCohortForTrails()!.cohortCode }}</h3>
                                <div style="display: flex; gap: 0.5rem;">
                                    <s25-ng-button [type]="'primary'" [onClick]="startCreateTrail">
                                        New {{ labels.TRAIL }}
                                    </s25-ng-button>
                                    <s25-ng-button [type]="'outline'" [onClick]="closeTrails">Close</s25-ng-button>
                                </div>
                            </div>
                            @if (trailsResource.isLoading()) {
                                <p>Loading trails…</p>
                            } @else if (!activeTrails().length) {
                                <p class="c-text--secondary">No trails in this cohort yet.</p>
                            } @else {
                                <table class="c-table">
                                    <thead>
                                        <tr>
                                            <th class="trail-cohort-admin__drag-col"></th>
                                            <th>Name</th>
                                            <th>Code</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        s25-ng-dnd-sortable
                                        [items]="sortableTrails()"
                                        (orderChanged)="onTrailOrderChanged($event)"
                                    >
                                        @for (trail of sortableTrails(); track trail.trailId; let trailIndex = $index) {
                                            <tr s25-ng-dnd-sortable-item [index]="trailIndex" [draggable]="true">
                                                <td class="trail-cohort-admin__drag-col">
                                                    <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="'Reorder trail'" />
                                                </td>
                                                <td>{{ trail.name }}</td>
                                                <td>{{ trail.trailCode }}</td>
                                                <td>
                                                    <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                                                        <s25-ng-button
                                                            [type]="'outline'"
                                                            [onClick]="editTrailFactory(trail)"
                                                        >
                                                            Edit
                                                        </s25-ng-button>
                                                        <s25-ng-button
                                                            [type]="'outline'"
                                                            [onClick]="forkTrailFactory(trail)"
                                                        >
                                                            Fork
                                                        </s25-ng-button>
                                                        <s25-ng-button
                                                            [type]="'outline'"
                                                            [onClick]="copyTrailFactory(trail)"
                                                        >
                                                            Copy
                                                        </s25-ng-button>
                                                        <s25-ng-button
                                                            [type]="'danger--outline'"
                                                            [onClick]="deleteTrailFactory(trail)"
                                                        >
                                                            Delete
                                                        </s25-ng-button>
                                                    </div>
                                                </td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }
                            @if (showTrailForm()) {
                                <div class="c-border c-padding--double c-margin-top--single">
                                    <h4>{{ editingTrail()?.trailId ? "Edit" : "Create" }} {{ labels.TRAIL }}</h4>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>Name *</label>
                                        <input
                                            class="c-input"
                                            [ngModel]="editingTrail()?.name ?? ''"
                                            (ngModelChange)="patchEditingTrailName($event)"
                                            required
                                        />
                                    </div>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>{{ labels.TRAIL }} Code *</label>
                                        <div>
                                            <input
                                                class="c-input"
                                                list="trail-cohort-admin-trail-code-suggestions"
                                                [ngModel]="editingTrail()?.trailCode ?? ''"
                                                (ngModelChange)="patchEditingTrailCode($event)"
                                                [disabled]="!!editingTrail()?.trailId"
                                                required
                                            />
                                            <datalist id="trail-cohort-admin-trail-code-suggestions">
                                                @for (code of existingTrailCodes(); track code) {
                                                    <option [value]="code"></option>
                                                }
                                            </datalist>
                                            @if (trailCodeConflict()) {
                                                <p class="c-text--error c-margin-top--half">
                                                    A trail with this code already exists in this cohort.
                                                </p>
                                            }
                                        </div>
                                    </div>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>Description</label>
                                        <textarea
                                            class="c-input"
                                            rows="2"
                                            [ngModel]="editingTrail()?.description ?? ''"
                                            (ngModelChange)="patchEditingTrailDescription($event)"
                                        ></textarea>
                                    </div>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>{{ labels.FOCUS_AREA }}</label>
                                        <select
                                            class="c-input"
                                            [ngModel]="editingTrail()?.focusAreaId ?? null"
                                            (ngModelChange)="patchEditingTrailFocusAreaId($event)"
                                        >
                                            <option [ngValue]="null">None</option>
                                            @for (area of focusAreasResource.value() ?? []; track area.focusAreaId) {
                                                <option [ngValue]="area.focusAreaId">{{ area.name }}</option>
                                            }
                                        </select>
                                    </div>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>Credits</label>
                                        <input
                                            type="number"
                                            class="c-input"
                                            data-id="trail-credits"
                                            min="0"
                                            step="0.01"
                                            [ngModel]="editingTrail()?.credits ?? ''"
                                            (ngModelChange)="patchEditingTrailCredits($event)"
                                        />
                                    </div>
                                    <div class="trail-cohort-admin__trail-tree c-margin-top--single">
                                        <h5>{{ labels.REQUIREMENTS }}</h5>
                                        <p class="c-text--secondary c-margin-bottom--half">
                                            Students must satisfy this tree to summit the
                                            {{ labels.TRAIL.toLowerCase() }}.
                                        </p>
                                        @if (trailTreeLoading()) {
                                            <p>Loading tree…</p>
                                        } @else if (selectedCohortForTrails()?.cohortId != null) {
                                            <s25-ng-trail-requirement-tree-editor
                                                [cohortId]="selectedCohortForTrails()!.cohortId"
                                                [node]="trailRequirementTree()"
                                                [domain]="'COCURRICULAR'"
                                                (nodeChange)="onTrailTreeChange($event)"
                                            />
                                        }
                                    </div>
                                    <div style="display: flex; gap: 0.75rem; margin-top: 0.5rem;">
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="saveTrail"
                                            [disabled]="trailCodeConflict()"
                                        >
                                            Save Trail
                                        </s25-ng-button>
                                        <s25-ng-button [type]="'danger--outline'" [onClick]="cancelTrailForm"
                                            >Cancel</s25-ng-button
                                        >
                                    </div>
                                </div>
                            }
                            @if (copyingTrail(); as sourceTrail) {
                                <div class="c-border c-padding--double c-margin-top--single">
                                    <h4>Copy {{ sourceTrail.name }}</h4>
                                    <p class="c-text--secondary c-margin-bottom--half">
                                        Copy this trail to another cohort, or fork a blank or full copy into this
                                        cohort.
                                    </p>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>Target {{ labels.COHORT }} *</label>
                                        <select
                                            class="c-input"
                                            [ngModel]="copyTrailTargetCohortId()"
                                            (ngModelChange)="onCopyTrailTargetChange($event)"
                                        >
                                            @for (cohort of copyTrailTargetCohorts(); track cohort.cohortId) {
                                                <option [ngValue]="cohort.cohortId">
                                                    {{ cohort.cohortCode }}
                                                    @if (cohort.cohortId === selectedCohortForTrails()?.cohortId) {
                                                        (this cohort)
                                                    }
                                                </option>
                                            }
                                        </select>
                                    </div>
                                    <fieldset class="c-margin-bottom--single" style="border: 0; margin: 0; padding: 0;">
                                        <legend style="font-weight: 600; margin-bottom: 0.25rem;">
                                            Requirement trees *
                                        </legend>
                                        @if (copyTrailReuseAvailable()) {
                                            <label
                                                style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;"
                                            >
                                                <input
                                                    type="radio"
                                                    name="copyTrailMode"
                                                    [checked]="copyTrailMode() === 'REUSE'"
                                                    (change)="copyTrailMode.set('REUSE')"
                                                />
                                                Reuse matching requirements
                                            </label>
                                        }
                                        <label
                                            style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;"
                                        >
                                            <input
                                                type="radio"
                                                name="copyTrailMode"
                                                [checked]="copyTrailMode() === 'FULL'"
                                                (change)="copyTrailMode.set('FULL')"
                                            />
                                            Copy all requirements
                                        </label>
                                        <label
                                            style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;"
                                        >
                                            <input
                                                type="radio"
                                                name="copyTrailMode"
                                                [checked]="copyTrailMode() === 'SHELL'"
                                                (change)="copyTrailMode.set('SHELL')"
                                            />
                                            Copy without requirements
                                        </label>
                                    </fieldset>
                                    <div style="display: flex; gap: 0.75rem; margin-top: 0.5rem;">
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="saveTrailCopy"
                                            [disabled]="!canCopyTrail()"
                                        >
                                            Copy Trail
                                        </s25-ng-button>
                                        <s25-ng-button [type]="'danger--outline'" [onClick]="cancelTrailCopy"
                                            >Cancel</s25-ng-button
                                        >
                                    </div>
                                </div>
                            }
                            @if (forkingTrail(); as sourceTrail) {
                                <div class="c-border c-padding--double c-margin-top--single">
                                    <h4>Fork {{ sourceTrail.name }}</h4>
                                    <p class="c-text--secondary c-margin-bottom--half">
                                        Creates a new trail in this cohort with a unique code and a copy of the
                                        requirement tree.
                                    </p>
                                    <div
                                        class="form-group c-margin-bottom--single"
                                        style="display: grid; grid-template-columns: 10rem minmax(0, 1fr); gap: 0.5rem; max-width: 36rem; align-items: center;"
                                    >
                                        <label>New {{ labels.TRAIL }} Code *</label>
                                        <div>
                                            <input
                                                class="c-input"
                                                list="trail-cohort-admin-fork-code-suggestions"
                                                [ngModel]="forkTrailCode()"
                                                (ngModelChange)="forkTrailCode.set($event)"
                                                placeholder="e.g. {{ sourceTrail.trailCode }}_honors"
                                                required
                                            />
                                            <datalist id="trail-cohort-admin-fork-code-suggestions">
                                                @for (code of existingTrailCodes(); track code) {
                                                    <option [value]="code"></option>
                                                }
                                            </datalist>
                                            @if (forkCodeConflict()) {
                                                <p class="c-text--error c-margin-top--half">
                                                    A trail with this code already exists in this cohort.
                                                </p>
                                            }
                                        </div>
                                    </div>
                                    <div style="display: flex; gap: 0.75rem; margin-top: 0.5rem;">
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="saveFork"
                                            [disabled]="forkCodeConflict() || !forkTrailCode().trim()"
                                        >
                                            Fork Trail
                                        </s25-ng-button>
                                        <s25-ng-button [type]="'danger--outline'" [onClick]="cancelFork"
                                            >Cancel</s25-ng-button
                                        >
                                    </div>
                                </div>
                            }
                        </section>
                    }

                    <section class="trail-cohort-admin__assign-section c-margin-top--double">
                        <h3>Student {{ labels.COHORT }} Assignment</h3>
                        <p class="c-text--secondary c-margin-bottom--half">
                            Search for a student, then assign or remove cohort membership and declarations.
                        </p>
                        <div class="trail-cohort-admin__contact-picker">
                            <label>Student</label>
                            <s25-ng-multiselect-search-criteria
                                [type]="'contacts'"
                                [buttonText]="'Search contacts'"
                                [modelBean]="contactPickerBean"
                                [selectedItems]="contactPickerBean.selectedItems ?? []"
                                [showResults]="true"
                                [popoverOnBody]="true"
                                [useSecurity]="false"
                                [singleSelect]="true"
                                (close)="onContactPickerClose($event)"
                                (changed)="onContactPickerClose($event)"
                            />
                        </div>

                        @if (selectedContactId()) {
                            <div class="trail-cohort-admin__assignments c-margin-top--single">
                                <div class="trail-cohort-admin__list-heading">
                                    <h4>Student's Current Assignments</h4>
                                    <s25-ng-button
                                        data-id="refresh-assignments"
                                        [type]="'outline'"
                                        [onClick]="refreshAssignments"
                                    >
                                        Refresh
                                    </s25-ng-button>
                                </div>
                                @if (assignmentsResource.isLoading()) {
                                    <p>Loading assignments…</p>
                                } @else if (!currentAssignments()?.length) {
                                    <p class="c-text--secondary">No cohort assignments for this student.</p>
                                } @else {
                                    <table class="c-table" data-id="assignments-table">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Code</th>
                                                <th>Assigned</th>
                                                <th>Basis</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            @for (row of currentAssignments(); track row.studentCohortId) {
                                                <tr>
                                                    <td>{{ row.cohortTypeCode }}</td>
                                                    <td>{{ row.cohortCode ?? "—" }}</td>
                                                    <td>{{ formatDateTime(row.assignedDt) }}</td>
                                                    <td>{{ row.assignmentBasis }}</td>
                                                    <td>
                                                        <s25-ng-button
                                                            [type]="'danger--outline'"
                                                            [onClick]="removeAssignmentFactory(row)"
                                                        >
                                                            Remove
                                                        </s25-ng-button>
                                                    </td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>
                                }

                                <div class="trail-cohort-admin__assign-form c-margin-top--single">
                                    <div class="form-group">
                                        <label>Assign this student to a {{ labels.COHORT }}</label>
                                        <select
                                            class="c-input"
                                            [ngModel]="assignCohortId()"
                                            (ngModelChange)="assignCohortId.set($event)"
                                        >
                                            <option [ngValue]="null">Select {{ labels.COHORT }}</option>
                                            @for (cohort of cohortList.value() ?? []; track cohort.cohortId) {
                                                <option [ngValue]="cohort.cohortId">
                                                    {{ cohort.cohortCode }} ({{ cohort.cohortTypeCode }})
                                                </option>
                                            }
                                        </select>
                                    </div>
                                    <s25-ng-button
                                        [type]="'primary'"
                                        [onClick]="assignStudent"
                                        [disabled]="!canAssign()"
                                    >
                                        Assign
                                    </s25-ng-button>
                                </div>

                                <div class="trail-cohort-admin__declarations c-margin-top--double">
                                    <h4>Student's {{ labels.CURRICULA }} declarations</h4>
                                    @if (declarationsResource.isLoading()) {
                                        <p>Loading declarations…</p>
                                    } @else if (!currentDeclarations()?.length) {
                                        <p class="c-text--secondary">No declarations for this student.</p>
                                    } @else {
                                        <table class="c-table" data-id="declarations-table">
                                            <thead>
                                                <tr>
                                                    <th>{{ labels.CURRICULUM }}</th>
                                                    <th>{{ labels.COHORT }}</th>
                                                    <th>{{ labels.TRACKS }}</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                @for (
                                                    declaration of currentDeclarations();
                                                    track declaration.declarationId
                                                ) {
                                                    <tr [attr.data-id]="'declaration-' + declaration.defnId">
                                                        <td>{{ declaration.displayName ?? declaration.defnId }}</td>
                                                        <td>{{ declaration.cohortCode ?? "—" }}</td>
                                                        <td>
                                                            @if (declaration.status === "ACTIVE") {
                                                                @if (declaration.availableTracks?.length) {
                                                                    <div class="trail-cohort-admin__decl-tracks">
                                                                        @for (
                                                                            track of declaration.availableTracks;
                                                                            track track.trackId
                                                                        ) {
                                                                            <label>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    [checked]="
                                                                                        isAdminTrackSelected(
                                                                                            declaration.defnId,
                                                                                            track.trackId
                                                                                        )
                                                                                    "
                                                                                    [disabled]="
                                                                                        isAdminTrackDisabled(
                                                                                            declaration.defnId,
                                                                                            track
                                                                                        )
                                                                                    "
                                                                                    (change)="
                                                                                        toggleAdminTrack(
                                                                                            declaration.defnId,
                                                                                            track.trackId
                                                                                        )
                                                                                    "
                                                                                />
                                                                                {{ track.name }}
                                                                            </label>
                                                                        }
                                                                        <s25-ng-button
                                                                            [type]="'outline'"
                                                                            data-id="save-declaration-tracks"
                                                                            [onClick]="
                                                                                saveDeclarationTracksFactory(
                                                                                    declaration
                                                                                )
                                                                            "
                                                                        >
                                                                            {{ labels.SAVE_TRACKS }}
                                                                        </s25-ng-button>
                                                                    </div>
                                                                } @else {
                                                                    —
                                                                }
                                                            }
                                                        </td>
                                                        <td>
                                                            @if (declaration.status === "ACTIVE") {
                                                                <s25-ng-button
                                                                    [type]="'danger--outline'"
                                                                    data-id="drop-declaration"
                                                                    [onClick]="dropDeclarationFactory(declaration)"
                                                                >
                                                                    {{ labels.DROP_DECLARATION }}
                                                                </s25-ng-button>
                                                            } @else {
                                                                Dropped
                                                            }
                                                        </td>
                                                    </tr>
                                                }
                                            </tbody>
                                        </table>
                                    }

                                    <div class="trail-cohort-admin__assign-form c-margin-top--single">
                                        <div class="form-group">
                                            <label>Add a {{ labels.CURRICULUM }} declaration for this student</label>
                                            <select
                                                class="c-input"
                                                data-id="add-declaration-defn"
                                                [ngModel]="addDefnId()"
                                                (ngModelChange)="onAddDefnIdChange($event)"
                                            >
                                                <option [ngValue]="null">Select {{ labels.CURRICULUM }}</option>
                                                @for (defn of publishedCurriculumOptions(); track defn.defnId) {
                                                    <option [ngValue]="defn.defnId">
                                                        {{ defn.displayName || defn.name }} ({{
                                                            defn.cohortCode ?? defn.cohortId
                                                        }})
                                                    </option>
                                                }
                                            </select>
                                        </div>
                                        @if (addTracksResource.value()?.length) {
                                            <div class="form-group">
                                                <label>{{ labels.TRACKS }}</label>
                                                @for (track of addTracksResource.value() ?? []; track track.trackId) {
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            [checked]="addTrackIds().includes(track.trackId)"
                                                            [disabled]="isAddTrackDisabled(track)"
                                                            (change)="toggleAddTrack(track.trackId)"
                                                        />
                                                        {{ track.name }}
                                                    </label>
                                                }
                                            </div>
                                        }
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="addDeclaration"
                                            [disabled]="addDefnId() == null"
                                            data-id="add-declaration-btn"
                                        >
                                            Add declaration
                                        </s25-ng-button>
                                    </div>
                                </div>
                            </div>
                        }
                    </section>
                </div>
            }
        </div>
    `,
    styles: `
        .trail-cohort-admin__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-cohort-admin__list-heading {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            margin-bottom: 0.75rem;
        }
        .trail-cohort-admin__list-heading h3,
        .trail-cohort-admin__list-heading h4 {
            margin: 0;
        }
        .c-table {
            width: 100%;
            border-collapse: collapse;
        }
        .c-table th,
        .c-table td {
            border: 1px solid var(--color--border, #ddd);
            padding: 0.4rem 0.6rem;
            text-align: left;
        }
        .trail-cohort-admin__form-section .form-group {
            display: grid;
            grid-template-columns: 12rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: center;
            max-width: 36rem;
        }
        .trail-cohort-admin__form-actions {
            display: flex;
            gap: 0.75rem;
            margin-top: 0.5rem;
        }
        .trail-cohort-admin__contact-picker {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-cohort-admin__contact-picker label {
            font-weight: 600;
        }
        .trail-cohort-admin__contact-picker s25-ng-multiselect-search-criteria {
            max-width: 28rem;
        }
        .trail-cohort-admin__assign-form {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 1rem;
        }
        .trail-cohort-admin__assign-form .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }
        .trail-cohort-admin__assign-form .c-input {
            min-width: 10rem;
        }
        .trail-cohort-admin__decl-tracks {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }
        .trail-cohort-admin__config-section .form-group {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
            max-width: 36rem;
        }
        /* Host class ngEditableCustomAttrSelector is globally position:absolute. */
        s25-custom-attr-dropdown.ngEditableCustomAttrSelector {
            position: relative;
            display: block;
            width: min(100%, 20rem);
        }
        .trail-cohort-admin__config-save {
            display: flex;
            align-items: center;
            gap: 1rem;
        }
        .trail-cohort-admin__avail-rule {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 6px;
            padding: 0.5rem 0.75rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            align-items: flex-start;
        }
        .trail-cohort-admin__rule-values {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .trail-cohort-admin__rule-value {
            display: flex;
            gap: 0.35rem;
            align-items: center;
        }
        .trail-cohort-admin__avail-rule .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }
        .trail-cohort-admin__avail-rule .c-input {
            min-width: 7rem;
        }
        .trail-cohort-admin__rule-actions {
            display: flex;
            gap: 0.75rem;
            margin-top: 0.5rem;
        }
        .trail-cohort-admin__drag-col {
            width: 1.75rem;
            text-align: center;
        }
        .trail-cohort-admin__trails-section tbody.sortable [s25-ng-dnd-sortable-item] {
            cursor: grab;
        }
        .trail-cohort-admin__trail-tree {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 6px;
            padding: 0.75rem 1rem;
        }
        .trail-cohort-admin__trail-tree h5 {
            margin: 0 0 0.25rem;
        }
    `,
})
export class TrailCohortAdminComponent {
    readonly labels = TrailLabels;
    private readonly userStore = inject(UserStore);

    formatDate(value: string | undefined | null): string {
        return formatUserprefDate(value, this.userStore.dateFormat());
    }

    formatDateTime(value: string | undefined | null): string {
        return formatUserprefDate(value, this.userStore.dateTimeFormat());
    }

    readonly cohortTypeCodePlaceholder = "e.g. UNDG";
    readonly cohortCodePlaceholder = `e.g. ${fallCohortCodeExample()}`;

    readonly backToAdmin = output<void>();

    handleBack = () => {
        this.backToAdmin.emit();
    };

    private readonly listGen = signal(0);

    refreshList = () => {
        this.listGen.update((count) => count + 1);
    };

    refreshAssignments = () => {
        this.assignmentsGen.update((count) => count + 1);
        this.declarationsGen.update((count) => count + 1);
    };

    readonly cohortList = s25Resource({
        params: () => this.listGen(),
        loader: () => TrailApi.getCohorts(),
    });

    readonly showForm = signal(false);
    readonly formCohortId = signal<number | null>(null);
    readonly formCohortTypeCode = signal("");
    readonly formCohortCode = signal("");
    readonly formEffectiveStart = signal("");
    readonly formEffectiveEnd = signal("");

    readonly copyingCohort = signal<CohortDto | null>(null);
    readonly copyFormTypeCode = linkedSignal(() => this.copyingCohort()?.cohortTypeCode ?? "");
    readonly copyFormCode = linkedSignal(() => {
        this.copyingCohort();
        return "";
    });
    readonly copyFormEffectiveStart = linkedSignal(() => toDateInputValue(this.copyingCohort()?.effectiveStartDt));
    readonly copyFormEffectiveEnd = linkedSignal(() => toDateInputValue(this.copyingCohort()?.effectiveEndDt));

    readonly configCodeCustAtrbId = linkedSignal(() => this.cohortConfigResource.value()?.cohortCodeCustAtrbId ?? null);
    readonly configTypeCustAtrbId = linkedSignal(() => this.cohortConfigResource.value()?.cohortTypeCustAtrbId ?? null);
    readonly configDateCustAtrbId = linkedSignal(() => this.cohortConfigResource.value()?.cohortDateCustAtrbId ?? null);
    readonly configSaveSuccessMessage = signal("");

    readonly contactItemTypeId = Item.Ids.Contact;
    readonly codeCustAtrbTypes = [CustomAttributes.type.Text];
    readonly dateCustAtrbTypes = [CustomAttributes.type.Date, CustomAttributes.type.DateTime];
    readonly dropdownItemFromId = dropdownItemFromId;

    readonly selectedCohortForRules = signal<CohortDto | null>(null);
    readonly editingRules = signal<CohortAvailabilityRule[]>([]);
    private readonly rulesReloadGen = signal(0);

    readonly selectedContactId = signal<number | null>(null);
    readonly assignCohortId = signal<number | null>(null);

    readonly selectedCohortForTrails = signal<CohortDto | null>(null);
    readonly trailListGen = signal(0);
    readonly showTrailForm = signal(false);
    readonly editingTrail = signal<TrailDto | null>(null);
    readonly forkingTrail = signal<TrailDto | null>(null);
    readonly forkTrailCode = signal("");
    readonly copyingTrail = signal<TrailDto | null>(null);
    readonly copyTrailMode = signal<CurricCopyMode>("FULL");
    readonly copyTrailTargetCohortId = signal<number | null>(null);

    readonly trailsResource = s25Resource({
        if: () => this.selectedCohortForTrails()?.cohortId != null,
        params: () => ({ cohortId: this.selectedCohortForTrails()?.cohortId, gen: this.trailListGen() }),
        loader: ({ params }) => TrailApi.getCohortTrails(params.cohortId!),
    });

    readonly trailTreeResource = s25Resource({
        if: () => this.showTrailForm() && this.editingTrail()?.rootGroupId != null,
        params: () => this.editingTrail()!.rootGroupId!,
        loader: ({ params }) => TrailApi.getRequirementRoot(params),
    });

    readonly trailTreeLoading = computed(
        () => this.editingTrail()?.rootGroupId != null && this.trailTreeResource.resource.isLoading(),
    );

    readonly trailRequirementTree = linkedSignal({
        source: () => {
            const rootGroupId = this.showTrailForm() ? (this.editingTrail()?.rootGroupId ?? null) : null;
            return {
                rootGroupId,
                detail: rootGroupId != null ? this.trailTreeResource.value() : null,
                cohortId: this.selectedCohortForTrails()?.cohortId,
            };
        },
        computation: ({ rootGroupId, detail, cohortId }) => {
            if (rootGroupId != null && detail) {
                return wireTreeToEditorNode(detail.requirementTree, cohortId);
            }
            return emptyTrailRequirementTree();
        },
    });

    readonly trailCodesResource = s25Resource({
        if: () => this.selectedCohortForTrails()?.cohortId != null,
        params: () => ({ cohortId: this.selectedCohortForTrails()?.cohortId, gen: this.trailListGen() }),
        loader: ({ params }) => TrailApi.getTrailCodes(params.cohortId!),
    });

    readonly existingTrailCodes = computed((): string[] => {
        return this.trailCodesResource.value() ?? [];
    });

    readonly trailCodeConflict = computed((): boolean => {
        const trail = this.editingTrail();
        if (!trail || trail.trailId) {
            return false;
        }
        return TrailApi.isTrailCodeTaken(trail.trailCode ?? "", this.existingTrailCodes());
    });

    readonly forkCodeConflict = computed((): boolean => {
        return TrailApi.isTrailCodeTaken(this.forkTrailCode(), this.existingTrailCodes());
    });

    readonly copyTrailTargetCohorts = computed((): CohortDto[] => {
        return (this.cohortList.value() ?? []).filter((cohort) => !cohort.isDisabled);
    });

    readonly copyTrailReuseAvailable = computed((): boolean => {
        const sourceCohortId = this.selectedCohortForTrails()?.cohortId;
        return this.copyTrailTargetCohortId() != null && this.copyTrailTargetCohortId() !== sourceCohortId;
    });

    readonly canCopyTrail = computed((): boolean => {
        if (!this.copyingTrail()?.trailId || this.copyTrailTargetCohortId() == null) {
            return false;
        }
        if (this.copyTrailMode() === "REUSE" && !this.copyTrailReuseAvailable()) {
            return false;
        }
        return true;
    });

    readonly activeTrails = computed((): TrailDto[] => {
        return (this.trailsResource.value() ?? [])
            .filter((trail) => !trail.isDisabled)
            .sort((trailA, trailB) => {
                const orderA = trailA.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
                const orderB = trailB.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
    });

    readonly sortableTrails = linkedSignal((): TrailDto[] => this.activeTrails());
    private trailOrderPersistHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly enqueueTrailOrderSave = createSortOrderSaveQueue();

    readonly focusAreasResource = s25Resource({
        loader: () => TrailApi.getFocusAreas(),
    });

    private readonly assignmentsGen = signal(0);

    readonly assignmentsResource = s25Resource({
        if: () => this.selectedContactId() != null,
        params: () => ({ contId: this.selectedContactId(), gen: this.assignmentsGen() }),
        loader: () => TrailApi.getStudentAssignments(this.selectedContactId()!),
    });

    readonly cohortConfigResource = s25Resource({
        loader: () => TrailApi.getCohortConfig(),
    });

    readonly matchCountResource = s25Resource({
        if: () => this.selectedCohortForRules()?.cohortId != null,
        params: () => ({ cohortId: this.selectedCohortForRules()?.cohortId, gen: this.rulesReloadGen() }),
        loader: ({ params }) => TrailApi.getCohortMatchCount(params.cohortId!),
    });

    readonly currentAssignments = computed((): StudentCohortDetailDto[] => {
        return this.assignmentsResource.value() ?? [];
    });

    private readonly declarationsGen = signal(0);

    readonly declarationsResource = s25Resource({
        if: () => this.selectedContactId() != null,
        params: () => ({ contId: this.selectedContactId(), gen: this.declarationsGen() }),
        loader: ({ params }) => TrailApi.listDeclarations(params.contId!),
    });

    readonly curriculaResource = s25Resource({
        loader: () => TrailApi.listCurricula(),
    });

    readonly addDefnId = signal<number | null>(null);
    readonly addTrackIds = signal<number[]>([]);

    readonly addTracksResource = s25Resource({
        if: () => this.addDefnId() != null,
        params: () => this.addDefnId(),
        loader: ({ params }) => TrailApi.listTracks(params!),
    });

    readonly publishedCurriculumOptions = computed((): (CurriculumListItem & { cohortCode?: string })[] => {
        const cohorts = this.cohortList.value() ?? [];
        const codeById = new Map(cohorts.map((cohort) => [cohort.cohortId, cohort.cohortCode]));
        return (this.curriculaResource.value() ?? [])
            .filter((defn) => defn.isPublished)
            .map((defn) => ({ ...defn, cohortCode: codeById.get(defn.cohortId) }));
    });

    readonly currentDeclarations = computed((): StudentDeclarationDto[] => {
        return this.declarationsResource.value() ?? [];
    });

    readonly adminDraftTrackIds = linkedSignal(() => {
        const byDefnId = new Map<number, number[]>();
        for (const declaration of this.currentDeclarations()) {
            byDefnId.set(
                declaration.defnId,
                (declaration.tracks ?? []).map((track) => track.trackId),
            );
        }
        return byDefnId;
    });

    readonly canAssign = computed(() => {
        return this.selectedContactId() != null && this.assignCohortId() != null;
    });

    contactPickerBean: MultiselectModelI = {
        uuid: "trail-cohort-admin-contact",
        singleSelect: true,
        title: "Contacts",
        selectedItems: [],
    };

    formatError(error: unknown): string {
        if (error instanceof Error) return error.message;
        return String(error ?? "Unknown error");
    }

    startCreateCohort = () => {
        this.copyingCohort.set(null);
        this.formCohortId.set(null);
        this.formCohortTypeCode.set("");
        this.formCohortCode.set("");
        this.formEffectiveStart.set("");
        this.formEffectiveEnd.set("");
        this.showForm.set(true);
    };

    editCohortFactory = (cohort: CohortDto) => {
        return () => {
            this.copyingCohort.set(null);
            this.formCohortId.set(cohort.cohortId ?? null);
            this.formCohortTypeCode.set(cohort.cohortTypeCode);
            this.formCohortCode.set(cohort.cohortCode);
            this.formEffectiveStart.set(toDateInputValue(cohort.effectiveStartDt));
            this.formEffectiveEnd.set(toDateInputValue(cohort.effectiveEndDt));
            this.showForm.set(true);
        };
    };

    cancelForm = () => {
        this.showForm.set(false);
    };

    disableCohortFactory = (cohort: CohortDto) => {
        return async () => {
            const confirmed = await AlertService.confirm(
                `Disable this ${TrailLabels.COHORT.toLowerCase()} "${cohort.cohortCode}"? It will no longer be assignable. This is a permanent action.`,
            );
            if (!confirmed) {
                return;
            }
            try {
                await TrailApi.saveCohort({ ...cohort, isDisabled: true });
                if (this.formCohortId() === cohort.cohortId) {
                    this.showForm.set(false);
                }
                if (this.copyingCohort()?.cohortId === cohort.cohortId) {
                    this.copyingCohort.set(null);
                }
                if (this.selectedCohortForTrails()?.cohortId === cohort.cohortId) {
                    this.selectedCohortForTrails.set(null);
                }
                this.listGen.update((count) => count + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    saveCohort = async () => {
        const typeCode = this.formCohortTypeCode().trim();
        const code = this.formCohortCode().trim();
        if (!typeCode || !code) {
            await AlertService.alert("Type Code and Code are required.");
            return;
        }
        const payload: CohortDto = {
            cohortId: this.formCohortId() ?? undefined,
            cohortTypeCode: typeCode,
            cohortCode: code,
            effectiveStartDt: this.formEffectiveStart() || undefined,
            effectiveEndDt: this.formEffectiveEnd() || undefined,
        };
        try {
            await TrailApi.saveCohort(payload);
            this.showForm.set(false);
            this.listGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    startCopyCohortFactory = (cohort: CohortDto) => {
        return () => {
            this.showForm.set(false);
            this.copyingCohort.set(cohort);
        };
    };

    cancelCopyCohort = () => {
        this.copyingCohort.set(null);
    };

    saveCopyCohort = async () => {
        const source = this.copyingCohort();
        const typeCode = this.copyFormTypeCode().trim();
        const code = this.copyFormCode().trim();
        if (!source?.cohortId || !typeCode || !code) {
            await AlertService.alert("Type Code and Code are required.");
            return;
        }
        const payload: {
            cohortTypeCode: string;
            cohortCode: string;
            effectiveStartDt?: string;
            effectiveEndDt?: string;
        } = {
            cohortTypeCode: typeCode,
            cohortCode: code,
        };
        if (this.copyFormEffectiveStart()) {
            payload.effectiveStartDt = this.copyFormEffectiveStart();
        }
        if (this.copyFormEffectiveEnd()) {
            payload.effectiveEndDt = this.copyFormEffectiveEnd();
        }
        try {
            await TrailApi.copyCohort(source.cohortId, payload);
            this.copyingCohort.set(null);
            this.listGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    saveCohortConfig = async () => {
        try {
            await TrailApi.saveCohortConfig({
                cohortCodeCustAtrbId: this.configCodeCustAtrbId() ?? undefined,
                cohortTypeCustAtrbId: this.configTypeCustAtrbId() ?? undefined,
                cohortDateCustAtrbId: this.configDateCustAtrbId() ?? undefined,
            });
            this.configSaveSuccessMessage.set("Configuration saved.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    showRulesFactory = (cohort: CohortDto) => {
        return async () => {
            this.selectedCohortForRules.set(cohort);
            try {
                const rules = await TrailApi.getCohortAvailabilityRules(cohort.cohortId!);
                this.editingRules.set(rules ?? []);
            } catch (caughtError) {
                this.editingRules.set([]);
            }
            this.rulesReloadGen.update((gen) => gen + 1);
        };
    };

    clearSelectedCohortForRules = () => {
        this.selectedCohortForRules.set(null);
        this.editingRules.set([]);
    };

    showTrailsFactory = (cohort: CohortDto) => {
        return () => {
            this.selectedCohortForTrails.set(cohort);
            this.trailListGen.update((gen) => gen + 1);
        };
    };

    closeTrails = () => {
        this.selectedCohortForTrails.set(null);
        this.showTrailForm.set(false);
        this.editingTrail.set(null);
        this.forkingTrail.set(null);
        this.forkTrailCode.set("");
    };

    startCreateTrail = () => {
        this.forkingTrail.set(null);
        this.forkTrailCode.set("");
        this.editingTrail.set({
            name: "",
            trailCode: "",
            description: "",
            catalogSortOrder: nextAppendSortOrder(this.activeTrails().map((trail) => trail.catalogSortOrder)),
        });
        this.showTrailForm.set(true);
    };

    editTrailFactory = (trail: TrailDto) => {
        return () => {
            this.forkingTrail.set(null);
            this.forkTrailCode.set("");
            this.editingTrail.set({ ...trail });
            this.showTrailForm.set(true);
        };
    };

    cancelTrailForm = () => {
        this.showTrailForm.set(false);
        this.editingTrail.set(null);
    };

    saveTrail = async () => {
        const cohort = this.selectedCohortForTrails();
        const trail = this.editingTrail();
        if (!cohort?.cohortId || !trail) return;
        if (!trail.name?.trim() || !trail.trailCode?.trim()) {
            await AlertService.alert("Name and trail code are required.");
            return;
        }
        if (!trail.trailId && this.trailCodeConflict()) {
            await AlertService.alert("A trail with this code already exists in this cohort.");
            return;
        }
        if (this.trailTreeResource.value()?.isPublished) {
            const confirmed = await AlertService.confirm(
                "This tree is published. Saving will affect active students. This is not a retroactive change. Continue?",
            );
            if (!confirmed) {
                return;
            }
        }
        try {
            await TrailApi.saveCohortTrail(cohort.cohortId, {
                ...trail,
                requirementTree: editorNodeToSaveTree(this.trailRequirementTree(), cohort.cohortId),
            });
            this.showTrailForm.set(false);
            this.editingTrail.set(null);
            this.trailListGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    onTrailTreeChange(updated: RequirementNode) {
        if (updated.nodeType === "group") {
            this.trailRequirementTree.set(updated);
        }
    }

    forkTrailFactory = (trail: TrailDto) => {
        return () => {
            this.showTrailForm.set(false);
            this.editingTrail.set(null);
            this.copyingTrail.set(null);
            this.forkingTrail.set(trail);
            this.forkTrailCode.set("");
            this.forkTrailCode.set("");
        };
    };

    cancelFork = () => {
        this.forkingTrail.set(null);
        this.forkTrailCode.set("");
    };

    saveFork = async () => {
        const sourceTrail = this.forkingTrail();
        const newCode = this.forkTrailCode().trim();
        if (!sourceTrail?.trailId || !newCode) {
            return;
        }
        if (this.forkCodeConflict()) {
            await AlertService.alert("A trail with this code already exists in this cohort.");
            return;
        }
        try {
            await TrailApi.forkTrail(sourceTrail.trailId, newCode);
            this.forkingTrail.set(null);
            this.forkTrailCode.set("");
            this.trailListGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    copyTrailFactory = (trail: TrailDto) => {
        return () => {
            this.forkingTrail.set(null);
            this.copyingTrail.set(trail);
            this.copyTrailMode.set("FULL");
            const sourceCohortId = this.selectedCohortForTrails()?.cohortId ?? null;
            const otherCohort = this.copyTrailTargetCohorts().find((cohort) => cohort.cohortId !== sourceCohortId);
            this.copyTrailTargetCohortId.set(otherCohort?.cohortId ?? sourceCohortId);
        };
    };

    onCopyTrailTargetChange(cohortId: number | string): void {
        this.copyTrailTargetCohortId.set(Number(cohortId));
        if (this.copyTrailMode() === "REUSE" && !this.copyTrailReuseAvailable()) {
            this.copyTrailMode.set("FULL");
        }
    }

    cancelTrailCopy = () => {
        this.copyingTrail.set(null);
        this.copyTrailTargetCohortId.set(null);
        this.copyTrailMode.set("FULL");
    };

    saveTrailCopy = async () => {
        const sourceTrail = this.copyingTrail();
        const targetCohortId = this.copyTrailTargetCohortId();
        if (!sourceTrail?.trailId || targetCohortId == null || !this.canCopyTrail()) {
            return;
        }
        try {
            await TrailApi.copyTrailToCohort(sourceTrail.trailId, targetCohortId, this.copyTrailMode());
            this.copyingTrail.set(null);
            this.copyTrailTargetCohortId.set(null);
            this.trailListGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    deleteTrailFactory = (trail: TrailDto) => {
        return async () => {
            const confirmed = await AlertService.confirm(`Delete trail "${trail.name}"? This cannot be undone.`);
            if (!confirmed) return;
            const cohort = this.selectedCohortForTrails();
            if (!cohort?.cohortId) return;
            try {
                await TrailApi.saveCohortTrail(cohort.cohortId, { ...trail, isDisabled: true });
                this.trailListGen.update((gen) => gen + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    patchEditingTrailName(name: string): void {
        const current = this.editingTrail();
        if (!current) {
            return;
        }
        this.editingTrail.set({ ...current, name });
    }

    patchEditingTrailCode(trailCode: string): void {
        const current = this.editingTrail();
        if (!current) {
            return;
        }
        this.editingTrail.set({ ...current, trailCode });
    }

    patchEditingTrailDescription(description: string): void {
        const current = this.editingTrail();
        if (!current) {
            return;
        }
        this.editingTrail.set({ ...current, description });
    }

    patchEditingTrailFocusAreaId(focusAreaId: number | null): void {
        const current = this.editingTrail();
        if (!current) {
            return;
        }
        this.editingTrail.set({ ...current, focusAreaId });
    }

    patchEditingTrailCredits(credits: number | string | null): void {
        const current = this.editingTrail();
        if (!current) {
            return;
        }
        const parsed = credits === "" || credits == null ? null : Number(credits);
        this.editingTrail.set({
            ...current,
            credits: parsed != null && Number.isFinite(parsed) ? parsed : null,
        });
    }

    onConfigCodeCustAtrbChange(choice: DropDownItem | null): void {
        this.configCodeCustAtrbId.set(idFromDropdownItem(choice) ?? null);
    }

    onConfigTypeCustAtrbChange(choice: DropDownItem | null): void {
        this.configTypeCustAtrbId.set(idFromDropdownItem(choice) ?? null);
    }

    onConfigDateCustAtrbChange(choice: DropDownItem | null): void {
        this.configDateCustAtrbId.set(idFromDropdownItem(choice) ?? null);
    }

    onRuleCustAtrbChange(ruleIndex: number, choice: DropDownItem | null): void {
        this.patchRule(ruleIndex, "custAtrbId", idFromDropdownItem(choice));
    }

    onTrailOrderChanged(trails: TrailDto[]): void {
        this.sortableTrails.set(trails);
        this.queueTrailOrderPersist();
    }

    private queueTrailOrderPersist(): void {
        if (this.trailOrderPersistHandle != null) {
            clearTimeout(this.trailOrderPersistHandle);
        }
        this.trailOrderPersistHandle = setTimeout(() => {
            this.trailOrderPersistHandle = null;
            void this.enqueueTrailOrderSave(() => this.persistTrailOrder());
        }, SORT_ORDER_PERSIST_DEBOUNCE_MS);
    }

    private async persistTrailOrder(): Promise<void> {
        const cohort = this.selectedCohortForTrails();
        if (!cohort?.cohortId) {
            return;
        }
        const trails = this.sortableTrails();
        const { orders, changes } = changedSortOrders(
            trails,
            (trail) => trail.trailId,
            (trail) => trail.catalogSortOrder,
        );
        if (!changes.length) {
            return;
        }
        const updated = trails.map((trail, trailIndex) => ({ ...trail, catalogSortOrder: orders[trailIndex] }));
        this.sortableTrails.set(updated);
        try {
            await TrailApi.updateTrailSortOrders(
                cohort.cohortId,
                changes.map((change) => ({ trailId: change.id, catalogSortOrder: change.sortOrder })),
            );
        } catch (caughtError) {
            if (this.sortableTrails() === updated) {
                this.sortableTrails.set(trails);
            }
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    patchRule<K extends keyof CohortAvailabilityRule>(
        ruleIndex: number,
        key: K,
        value: CohortAvailabilityRule[K],
    ): void {
        const rules = this.editingRules();
        this.editingRules.set(rules.map((rule, idx) => (idx === ruleIndex ? { ...rule, [key]: value } : rule)));
    }

    onRuleOperatorChange(ruleIndex: number, operator: AvailabilityOperator): void {
        const rules = this.editingRules();
        this.editingRules.set(
            rules.map((rule, idx) => {
                if (idx !== ruleIndex) {
                    return rule;
                }
                if (operator === "EXISTS" || operator === "NOT_EXISTS") {
                    return { ...rule, operator, values: [] };
                }
                if (operator === "CONTAINS") {
                    const firstValue = (rule.values ?? []).map((value) => value.trim()).find(Boolean) ?? "";
                    return { ...rule, operator, values: [firstValue] };
                }
                const existingValues = rule.values ?? [];
                const nextValues = existingValues.length ? [...existingValues] : [""];
                return { ...rule, operator, values: nextValues };
            }),
        );
    }

    patchContainsValue(ruleIndex: number, value: string): void {
        this.patchRule(ruleIndex, "values", [value]);
    }

    patchRuleValueAt(ruleIndex: number, valueIndex: number, value: string): void {
        const rules = this.editingRules();
        this.editingRules.set(
            rules.map((rule, idx) => {
                if (idx !== ruleIndex) {
                    return rule;
                }
                const values = [...(rule.values ?? [])];
                values[valueIndex] = value;
                return { ...rule, values };
            }),
        );
    }

    addRuleValue(ruleIndex: number): void {
        const rules = this.editingRules();
        this.editingRules.set(
            rules.map((rule, idx) => (idx === ruleIndex ? { ...rule, values: [...(rule.values ?? []), ""] } : rule)),
        );
    }

    removeRuleValue(ruleIndex: number, valueIndex: number): void {
        const rules = this.editingRules();
        this.editingRules.set(
            rules.map((rule, idx) => {
                if (idx !== ruleIndex) {
                    return rule;
                }
                const values = [...(rule.values ?? [])];
                values.splice(valueIndex, 1);
                return { ...rule, values };
            }),
        );
    }

    removeRule(ruleIndex: number): void {
        this.editingRules.set(this.editingRules().filter((_, idx) => idx !== ruleIndex));
    }

    addRule = () => {
        const rules = this.editingRules();
        this.editingRules.set([
            ...rules,
            {
                operator: "IN" as const,
                values: [""],
                catalogDescription: "",
                sortOrder: nextAppendSortOrder(rules.map((rule) => rule.sortOrder)),
            },
        ]);
    };

    saveRules = async () => {
        const cohort = this.selectedCohortForRules();
        if (!cohort?.cohortId) return;
        const incomplete = this.editingRules().some(
            (rule) => rule.custAtrbId == null || !Number.isFinite(rule.custAtrbId),
        );
        if (incomplete) {
            await AlertService.alert("Each rule needs a custom attribute.");
            return;
        }
        const cleanedRules = this.editingRules().map((rule) => this.cleanedRule(rule));
        const uniqueOrders = uniqueSortOrdersForInsertSet(cleanedRules.map((rule) => rule.sortOrder));
        const rulesToSave = cleanedRules.map((rule, ruleIndex) => ({
            ...rule,
            sortOrder: uniqueOrders[ruleIndex],
        }));
        try {
            await TrailApi.saveCohortAvailabilityRules(cohort.cohortId, rulesToSave);
            this.editingRules.set(rulesToSave);
            this.rulesReloadGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    private cleanedRule(rule: CohortAvailabilityRule): CohortAvailabilityRule {
        if (rule.operator === "EXISTS" || rule.operator === "NOT_EXISTS") {
            return { ...rule, values: [] };
        }
        const values = (rule.values ?? []).map((value) => value.trim()).filter(Boolean);
        if (rule.operator === "CONTAINS") {
            return { ...rule, values: values.slice(0, 1) };
        }
        return { ...rule, values };
    }

    onContactPickerClose(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        const contactItemId = first?.itemId != null ? Number(first.itemId) : null;
        const nextContactId = Number.isFinite(contactItemId) && contactItemId! > 0 ? contactItemId : null;
        this.contactPickerBean.selectedItems = [...items];
        if (nextContactId === this.selectedContactId()) {
            return;
        }
        this.selectedContactId.set(nextContactId);
        this.assignCohortId.set(null);
        this.addDefnId.set(null);
        this.addTrackIds.set([]);
        this.assignmentsGen.update((gen) => gen + 1);
        this.declarationsGen.update((gen) => gen + 1);
    }

    onAddDefnIdChange(raw: number | string | null): void {
        const defnId = raw == null || raw === "null" ? null : Number(raw);
        this.addDefnId.set(Number.isFinite(defnId) ? defnId : null);
        this.addTrackIds.set([]);
    }

    isAdminTrackSelected(defnId: number, trackId: number): boolean {
        return (this.adminDraftTrackIds().get(defnId) ?? []).includes(trackId);
    }

    isAdminTrackDisabled(defnId: number, track: { trackId: number; cannotCombineWith?: S25ItemI.NumId[] }): boolean {
        if (this.isAdminTrackSelected(defnId, track.trackId)) return false;
        const selectedIds = this.adminDraftTrackIds().get(defnId) ?? [];
        const exclusiveIds = (track.cannotCombineWith ?? []).map((item) => Number(item.itemId));
        return exclusiveIds.some((exclusiveId) => selectedIds.includes(exclusiveId));
    }

    toggleAdminTrack(defnId: number, trackId: number): void {
        this.adminDraftTrackIds.update((current) => {
            const next = new Map(current);
            const selected = new Set(next.get(defnId) ?? []);
            if (selected.has(trackId)) selected.delete(trackId);
            else selected.add(trackId);
            next.set(defnId, [...selected]);
            return next;
        });
    }

    isAddTrackDisabled(track: TrackDetail): boolean {
        if (this.addTrackIds().includes(track.trackId)) return false;
        const exclusiveIds = (track.cannotCombineWith ?? []).map((item) => Number(item.itemId));
        return exclusiveIds.some((exclusiveId) => this.addTrackIds().includes(exclusiveId));
    }

    toggleAddTrack(trackId: number): void {
        const selected = new Set(this.addTrackIds());
        if (selected.has(trackId)) selected.delete(trackId);
        else selected.add(trackId);
        this.addTrackIds.set([...selected]);
    }

    addDeclaration = async () => {
        const contId = this.selectedContactId();
        const defnId = this.addDefnId();
        if (contId == null || defnId == null) return;
        try {
            await TrailApi.adminDeclare(contId, defnId, this.addTrackIds());
            this.addDefnId.set(null);
            this.addTrackIds.set([]);
            this.declarationsGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    saveDeclarationTracksFactory = (declaration: StudentDeclarationDto) => {
        return async () => {
            const contId = this.selectedContactId();
            if (contId == null) return;
            const nextIds = this.adminDraftTrackIds().get(declaration.defnId) ?? [];
            const currentIds = (declaration.tracks ?? []).map((track) => track.trackId);
            const removing = currentIds.filter((trackId) => !nextIds.includes(trackId));
            if (removing.length) {
                const confirmed = await AlertService.confirm(
                    `Remove ${removing.length === 1 ? "this track" : "these tracks"} from the declaration? Earned credit is kept.`,
                );
                if (!confirmed) return;
            }
            try {
                await TrailApi.adminReplaceTracks(contId, declaration.defnId, nextIds);
                this.declarationsGen.update((gen) => gen + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    dropDeclarationFactory = (declaration: StudentDeclarationDto) => {
        return async () => {
            const contId = this.selectedContactId();
            if (contId == null) return;
            const confirmed = await AlertService.confirm(`Drop this ${TrailLabels.CURRICULUM}? Earned credit is kept.`);
            if (!confirmed) return;
            try {
                await TrailApi.adminDropDeclaration(contId, declaration.defnId);
                this.declarationsGen.update((gen) => gen + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    assignStudent = async () => {
        const contId = this.selectedContactId();
        const cohortId = this.assignCohortId();
        if (contId == null || cohortId == null) return;
        try {
            await TrailApi.assignStudentCohorts([{ contId, cohortId }]);
            this.assignCohortId.set(null);
            this.assignmentsGen.update((gen) => gen + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    removeAssignmentFactory = (row: StudentCohortDetailDto) => {
        return async () => {
            const confirmed = await AlertService.confirm(
                `Remove this student from ${row.cohortCode ?? row.cohortTypeCode}?`,
            );
            if (!confirmed) return;
            try {
                await TrailApi.unassignStudentCohorts([{ contId: row.contId, cohortId: row.cohortId }]);
                this.assignmentsGen.update((gen) => gen + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };
}
